/**
 * ListingGallery Component (Client Island)
 *
 * Diamond Architecture: Client Component for carousel interactivity.
 * Displays listing images with navigation.
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import { plainText, type LocalizedText, type S3Key } from '@letsgethome/types/branded';
import { Button } from '@letsgethome/ui/button';
import { CardControlLayer } from '@letsgethome/ui/card-layers';
import { cn } from '@letsgethome/ui/lib/utils';
import { S3Image } from '@letsgethome/ui/s3-image';
import { UnstyledButton } from '@letsgethome/ui/unstyled-button';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

type Props = {
  images: S3Key[];
  alt?: LocalizedText | '';
  onImageClick?: (index: number) => void;
};

export function ListingGallery({ images, alt = '', onImageClick }: Props) {
  const t = useTranslations('listings.gallery');
  const tA11y = useTranslations('common');
  const [currentIndex, setCurrentIndex] = useState(0);

  const hasImages = images.length > 0;
  const hasMultiple = images.length > 1;

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  if (!hasImages) {
    return (
      <div className="relative aspect-[16/9] w-full overflow-hidden rounded-xl bg-muted">
        <div className="flex h-full items-center justify-center text-muted-foreground">
          {t('noImages')}
        </div>
      </div>
    );
  }

  const currentImage = images[currentIndex];
  if (!currentImage) return null;

  return (
    <div className="relative">
      {/* Main Image */}
      <div className="relative aspect-[16/9] w-full overflow-hidden rounded-xl bg-muted">
        {/* Clickable image overlay — the stretched control, on the overlay
            layer. The arrows and the counter sit in `CardControlLayer` so
            "controls paint above the overlay" is the seam's one decision
            (LET-85), not a z-index picked here. */}
        <UnstyledButton
          className={cn('absolute inset-0 cursor-pointer', Z_LAYERS.cardOverlayLink.className)}
          onClick={() => onImageClick?.(currentIndex)}
          type="button"
          aria-label={tA11y('a11y.viewAllImages')}
        />
        <S3Image
          src={currentImage}
          alt={plainText(`${alt} - ${currentIndex + 1}`)}
          fill
          className="object-cover"
          preload={currentIndex === 0}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
        />

        {/* Navigation Arrows */}
        {hasMultiple && (
          <>
            <CardControlLayer className="left-4 top-1/2 -translate-y-1/2">
              <Button
                variant="secondary"
                size="icon"
                className="rounded-full bg-white/90 shadow-md hover:bg-white"
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                aria-label={tA11y('a11y.previousImage')}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            </CardControlLayer>
            <CardControlLayer className="right-4 top-1/2 -translate-y-1/2">
              <Button
                variant="secondary"
                size="icon"
                className="rounded-full bg-white/90 shadow-md hover:bg-white"
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                aria-label={tA11y('a11y.nextImage')}
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </CardControlLayer>
          </>
        )}

        {/* Image Counter */}
        {hasMultiple && (
          <CardControlLayer className="bottom-4 right-4 rounded-full bg-black/60 px-3 py-1 text-sm text-white">
            {currentIndex + 1} / {images.length}
          </CardControlLayer>
        )}
      </div>

      {/* Thumbnail Strip (Desktop) */}
      {hasMultiple && (
        <div className="mt-4 hidden gap-2 md:flex">
          {images.slice(0, 6).map((image, index) => (
            <UnstyledButton
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={cn(
                'relative h-20 w-32 overflow-hidden rounded-lg transition-opacity',
                currentIndex === index ? 'ring-2 ring-foreground' : 'opacity-70 hover:opacity-100'
              )}
            >
              <S3Image
                src={image}
                alt={plainText(`Thumbnail ${index + 1}`)}
                fill
                className="object-cover"
                sizes="128px"
              />
            </UnstyledButton>
          ))}
          {images.length > 6 && (
            <UnstyledButton
              onClick={() => setCurrentIndex(6)}
              className="flex h-20 w-32 items-center justify-center rounded-lg bg-muted text-sm text-muted-foreground hover:bg-accent"
            >
              {t('morePhotos', { count: images.length - 6 })}
            </UnstyledButton>
          )}
        </div>
      )}
    </div>
  );
}
